import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  cancel_order,
  get_order_details,
  messageClear,
} from "../../store/reducers/orderReducer";
import CurrencyFormat from "../shared/CurrencyFormat";
import ConfirmationModal from "../shared/ConfirmationModal";
import toast from "react-hot-toast";

// Componente para mostrar el estado con un badge
const StatusBadge = ({ status, type }) => {
  const getStatusConfig = () => {
    if (type === "payment") {
      switch (status) {
        case "paid":
          return {
            backgroundColor: "bg-green-300",
            textColor: "text-green-800",
            text: "Pagado",
          };
        case "pending":
          return {
            backgroundColor: "bg-yellow-300",
            textColor: "text-yellow-800",
            text: "Pendiente",
          };
        case "cancelled":
          return {
            backgroundColor: "bg-red-300",
            textColor: "text-red-800",
            text: "Cancelado",
          };
        default:
          return {
            backgroundColor: "bg-gray-300",
            textColor: "text-gray-800",
            text: "Desconocido",
          };
      }
    }

    // Delivery status
    switch (status) {
      case "completed":
        return {
          backgroundColor: "bg-green-300",
          textColor: "text-green-800",
          text: "Entregado",
        };
      case "processing":
        return {
          backgroundColor: "bg-blue-300",
          textColor: "text-blue-800",
          text: "Lista",
        };
      case "preparing":
        return {
          backgroundColor: "bg-yellow-300",
          textColor: "text-yellow-800",
          text: "Preparando",
        };
      case "new order":
        return {
          backgroundColor: "bg-purple-300",
          textColor: "text-purple-800",
          text: "Orden Nueva",
        };
      case "cancelled":
        return {
          backgroundColor: "bg-red-300",
          textColor: "text-red-800",
          text: "Cancelada",
        };
      default:
        return {
          backgroundColor: "bg-gray-300",
          textColor: "text-gray-800",
          text: "Estado Desconocido",
        };
    }
  };

  const { backgroundColor, textColor, text } = getStatusConfig();

  return (
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium ${backgroundColor} ${textColor}`}
    >
      {text}
    </span>
  );
};

// Componente para mostrar la información del producto
const ProductItem = ({ product }) => {
  const { name, brand, quantity, price, discount, images } = product;
  const finalPrice = price - Math.floor((price * discount) / 100);

  return (
    <div className="h-28 flex justify-between items-center text-slate-600 p-4 border rounded-lg hover:bg-gray-50">
      <div className="flex gap-2 flex-grow">
        <img
          className="w-[55px] h-[55px] object-cover rounded-md"
          src={images[0]}
          alt={name}
        />
        <div className="flex flex-col justify-start">
          <Link className="hover:text-blue-600 font-medium">{name}</Link>
          <p className="text-sm">{brand}</p>
          <p className="text-sm">Cantidad: {quantity}</p>
        </div>
      </div>
      <div className="text-right">
        <h2
          className={
            "font-semibold text-sm " + (discount > 0 ? "text-green-600" : "")
          }
        >
          <CurrencyFormat value={finalPrice} />
        </h2>
        {discount > 0 && (
          <>
            <div className="line-through text-sm text-gray-500">
              <CurrencyFormat value={price} />
            </div>
            <p className="text-sm text-red-600">-{discount}%</p>
          </>
        )}
      </div>
    </div>
  );
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { myOrder, successMessage, errorMessage } = useSelector(
    (state) => state.order
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(get_order_details(orderId));
  }, [orderId, dispatch]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
      dispatch(get_order_details(orderId));
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch, orderId]);

  const handleCancelOrder = async () => {
    setIsLoading(true);
    try {
      await dispatch(cancel_order(orderId));
    } catch (error) {
      console.error("Error al cancelar la orden:", error);
    } finally {
      setIsLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <div className="bg-white p-5 rounded-lg shadow-sm max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-slate-700">
          Detalles de la Orden
        </h2>
        {myOrder.delivery_status === "new order" &&
          myOrder.delivery_status !== "preparing" && (
            <button
              className="outline-none px-3 py-1 border rounded-md text-slate-600 shadow-md bg-slate-100 hover:border-slate-300 hover:shadow-red-200 hover:bg-red-300 hover:text-white active:bg-red-400"
              onClick={() => setIsModalOpen(true)}
            >
              Cancelar Orden
            </button>
          )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Información de envío */}
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-slate-700 mb-2">
              Información de Entrega
            </h3>
            <div className="space-y-2">
              <div>
                <p className="text-slate-600">
                  Recibe: {myOrder.shippingInfo?.name}
                </p>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                  Dirección de Envío
                </span>
                <div className="mt-2 text-slate-600 space-y-1">
                  <div>{myOrder.shippingInfo?.address}</div>
                  <div>{myOrder.shippingInfo?.province}</div>
                  <div>{myOrder.shippingInfo?.city}</div>
                </div>
              </div>
              <p className="text-slate-600">Correo: {userInfo.email}</p>
            </div>
          </div>
        </div>

        {/* Información de pago y estado */}
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <h3 className="font-medium text-slate-700 mb-2">
            Resumen de la Orden
          </h3>
          <div className="text-slate-600">
            Precio Total: <CurrencyFormat value={myOrder.price} />
          </div>
          <div className="flex items-center gap-2">
            Estado del Pago:
            <StatusBadge status={myOrder.payment_status} type="payment" />
          </div>
          <div className="flex items-center gap-2">
            Estado de la Orden:
            <StatusBadge status={myOrder.delivery_status} type="delivery" />
          </div>
        </div>
      </div>

      <p className="text-slate-600 my-4">ID de Orden: {myOrder._id}</p>

      {/* Lista de productos */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-slate-700 mb-4">
          Productos de la Orden
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {myOrder.products?.map((product, index) => (
            <ProductItem key={index} product={product} />
          ))}
        </div>
      </div>

      {/* Modal de Confirmación */}
      <ConfirmationModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        isLoading={isLoading}
        onConfirm={handleCancelOrder}
        title="Cancelar Orden"
        description="¿Está seguro de que desea cancelar esta orden? Esta acción no se puede deshacer."
        confirmText="Sí, cancelar"
        cancelText="No, volver"
      />
    </div>
  );
};

export default OrderDetails;
